import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { useFieldArray, useFormContext, Controller } from 'react-hook-form'
import { ApiAutoComplete } from '../../../../../components/FormElements/ApiAutoComplete'
import GenericButton from '../../../../../components/GenericButton'
import { ButtonType, ButtonSize } from '../../../../../components/GenericButton/styles'
import { black, grey, red, shade } from '../../../../../colors'
import { ReactComponent as Trash } from '../../../../../assets/trash.svg'
import GenericAutocomplete from '../../../../../components/FormElements/FormAutoComplete'
import { useParams, useSearchParams } from 'react-router-dom'
import CustomTooltip from '../../../../../components/CustomTooltip'

interface ComparisonField {
  compareBy: string | null
  comparison: string | null
}

type FormValues = Record<string, ComparisonField[]>

interface ComparisonsSectionProps {
  name: keyof FormValues
  control: any
}

const ComparisonsSection: React.FC<ComparisonsSectionProps> = ({ name, control }) => {
  const theme = useTheme()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const seriesId = searchParams.get('projectId')
  const termId = searchParams.get('termId')

  const params = { termId, distributionSeriesId: seriesId, pageSize: 200 }
  const { watch } = useFormContext<FormValues>()
  const { fields, append, remove } = useFieldArray<FormValues, typeof name>({
    control,
    name
  })
  const { resetField, setValue } = useFormContext<FormValues>()

  const isSingleComparison = fields.length === 1
  const maxComparisons = 5

  const options = [
    { title: 'Instructor', value: 'instructor' },
    { title: 'Academic Unit', value: 'academic unit' }
  ]

  const getAddComparisonTooltip = (canAddMore: boolean): string => {
    if (canAddMore) return ''
    return 'You can only add up to 5 comparisons.'
  }

  const watchedComparisons = watch(name) || []
  const canAddMore = fields.length < maxComparisons

  return (
    <>
      <Typography color={black[100]} fontWeight={700}>Compare Data</Typography>

      {fields.map((field, index) => {
        const selectedComparisons = watchedComparisons
          .map((f, i) => (i !== index ? f.comparison : null))
          .filter(Boolean)

        return (
          <Box key={field.id} padding={theme.spacing(4, 4)} borderRadius={theme.spacing(2)} bgcolor={shade[750]} display="flex" flexDirection="column" gap={1.5} >
            <Typography color={grey[950]} fontWeight={500}>Compare By</Typography>
            <GenericAutocomplete
              key={`compareBy-${field.id}-${control._formValues?.[name]?.[index]?.compareBy ?? ''}`}
              name={`${name}.${index}.compareBy`}
              options={options}
              placeholder="Select Academic Unit or Instructor"
              getOptionValue={(option) => option?.value}
              getOptionLabel={(option) => option?.title}
              getOptionDisabled={(option) => option?.value === 'instructor'}
              textFieldStyles={{ bgcolor: 'white' }}
            />

            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Select Comparison</Typography>
            <ApiAutoComplete
              key={`comparison-${field.id}-${control._formValues?.[name]?.[index]?.comparison ?? ''}`}
              name={`${name}.${index}.comparison`}
              apiEndpoint={`/api/divisions/${id}/reports/search/`}
              placeholder="Select Academic Unit or Instructor"
              valueField="divisionId"
              labelField="title"
              apiParams={params}
              getOptionDisabled={option => selectedComparisons.includes(option.id)}
              getOptionSubLabel={option => option?.abbreviation}
              wrapperStyle={{ minHeight: `${theme.spacing(15)} !important` }}
              clearIcon={false}
              textFieldStyles={{ bgcolor: 'white' }}
            />

            <Box display="flex" justifyContent="flex-end" width="100%" mt={-2} >
              <GenericButton
                buttonType={ButtonType.Ghost}
                buttonSize={ButtonSize.Small}
                startIcon={<Trash />}
                label={'Remove Comparison'}
                onClick={() => {
                  if (isSingleComparison) {
                    setValue(`${name}.0.compareBy`, null)
                    setValue(`${name}.0.comparison`, null)
                  } else {
                    remove(index)
                  }
                }}
                disabled={false}
                style={{
                  color: red[600],
                  fontWeight: 600,
                  backgroundColor: 'transparent',
                  padding: theme.spacing(1, 0)
                }}
              />
            </Box>
          </Box>
        )
      })}
      <Box display="flex" justifyContent="flex-end" >
        <Box display="flex" justifyContent="flex-end" >
          <CustomTooltip
            title={getAddComparisonTooltip(canAddMore)}
            placement="top"
          >
            <GenericButton
              buttonType={ButtonType.Ghost}
              buttonSize={ButtonSize.Small}
              label={'+ Add Comparison'}
              style={{ fontWeight: 600, fontSize: '16px', cursor: canAddMore ? 'pointer' : 'not-allowed' }}
              onClick={canAddMore ? () => { append({ compareBy: null, comparison: null }) } : undefined}
              disabled={!canAddMore}
            />
          </CustomTooltip>
        </Box>
      </Box>
    </>
  )
}

export default ComparisonsSection
